import { defineStore } from 'pinia';
import * as sessao from '@/services/api/deck/sessao.js';
import * as apiProjetos from '@/services/api/deck/projetos.js';

const CHAVE_PROJETO = 'deck:projeto';

/**
 * O estado que toda tela consulta: quem sou aqui, os projetos, e as tabelas de
 * apoio do projeto ativo.
 *
 * Carregado UMA vez no boot. Sem isso, cada formulario buscaria a lista de
 * pessoas e de etiquetas de novo -- e sao as mesmas listas.
 *
 * O `projetoAtivo` existe porque quadro, backlog e sprint sao POR PROJETO, mas
 * `nav.route` precisa de um caminho estatico. A tela `/quadro` resolve o
 * ultimo projeto usado e REDIRECIONA para `/quadro/:key`: a barra de enderecos
 * nunca fica ambigua, e o link colado no chat sempre aponta para um projeto.
 */
export const useDeckStore = defineStore('deck', {
    state: () => ({
        membro: null,
        corp: null,
        permissoes: { manage: false, configure: false },
        // O desvio entre o relogio desta maquina e o do servidor, em ms. E com
        // ele que o timer calcula o decorrido -- sem isso, um notebook quatro
        // minutos adiantado mostra timer negativo, e quem estava certo era o
        // servidor.
        desvioDoRelogio: 0,

        vocabulario: null,
        projetos: [],
        pessoas: [],
        times: [],

        projetoAtivo: null,
        statusDoProjeto: [],
        tiposDoProjeto: [],
        quadrosDoProjeto: [],
        etiquetas: [],

        carregando: false,
        erro: null,
    }),

    getters: {
        chaveDoProjetoAtivo: (state) => state.projetoAtivo?.key ?? null,

        /** O ultimo projeto usado, ou o primeiro da lista. */
        projetoPadrao(state) {
            const lembrado = lerLembrado();

            return state.projetos.find((p) => p.key === lembrado) ?? state.projetos[0] ?? null;
        },

        estimaEmPontos: (state) => state.projetoAtivo?.estimation_unit !== 'hours',

        pessoasAtivas: (state) => state.pessoas.filter((p) => p.active),
    },

    actions: {
        async carregar() {
            this.carregando = true;
            this.erro = null;

            try {
                const [eu, vocabulario, projetos, pessoas, times] = await Promise.all([
                    sessao.me(),
                    sessao.vocabulario(),
                    apiProjetos.listar(),
                    sessao.membros(),
                    sessao.times(),
                ]);

                this.aplicarSessao(eu);
                this.vocabulario = vocabulario;
                this.projetos = projetos.data ?? [];
                this.pessoas = pessoas.data ?? [];
                this.times = times.data ?? [];
            } catch (erro) {
                this.erro = erro;
                throw erro;
            } finally {
                this.carregando = false;
            }
        },

        aplicarSessao(resposta) {
            this.membro = resposta.member ?? null;
            this.corp = resposta.corp ?? null;
            this.permissoes = resposta.permissions ?? { manage: false, configure: false };

            if (resposta.server_time) {
                this.desvioDoRelogio = Date.now() - Date.parse(resposta.server_time);
            }
        },

        /** O agora do SERVIDOR, visto desta maquina. */
        agora() {
            return Date.now() - this.desvioDoRelogio;
        },

        /**
         * Troca o projeto ativo e carrega as tabelas de apoio dele.
         *
         * Idempotente: chamar com a chave ja ativa nao refaz a consulta -- a
         * tela chama isto em todo `route.params.projectKey`, inclusive quando
         * so o filtro mudou.
         */
        async ativarProjeto(chave, { forcar = false } = {}) {
            if (!chave) {
                return null;
            }

            const normalizada = String(chave).toUpperCase();

            if (!forcar && this.projetoAtivo?.key === normalizada) {
                return this.projetoAtivo;
            }

            const resposta = await apiProjetos.ver(normalizada);

            this.projetoAtivo = resposta.data;
            this.statusDoProjeto = resposta.data?.statuses ?? [];
            this.tiposDoProjeto = resposta.data?.types ?? [];
            this.quadrosDoProjeto = resposta.data?.boards ?? [];
            this.permissoesNoProjeto = resposta.can ?? {};

            const etiquetas = await sessao.etiquetas({ project: normalizada });
            this.etiquetas = etiquetas.data ?? [];

            lembrar(normalizada);

            return this.projetoAtivo;
        },

        statusPorId(id) {
            return this.statusDoProjeto.find((s) => s.id === id) ?? null;
        },

        tipoPorSlug(slug) {
            return this.tiposDoProjeto.find((t) => t.slug === slug) ?? null;
        },

        pessoaPorId(id) {
            return this.pessoas.find((p) => p.id === id) ?? null;
        },

        /** Some com o estado do tenant anterior ao trocar de corporacao. */
        limpar() {
            this.$reset();
        },
    },
});

function lembrar(chave) {
    try {
        localStorage.setItem(CHAVE_PROJETO, chave);
    } catch {
        // Janela anonima, storage cheio, navegador que bloqueia: a aplicacao
        // segue sem lembrar o projeto. Nao e motivo para quebrar a tela.
    }
}

function lerLembrado() {
    try {
        return localStorage.getItem(CHAVE_PROJETO);
    } catch {
        return null;
    }
}
